<template>
  <div>
    <CollapsableSection
      :title="displayName || facet.name"
      :is-open="isFilterListExpanded"
      collapsable-content-classes="px-6 pb-4 pt-2 border-b border-solid border-gray-200"
      data-test="attribute-filter"
      @update:open="setFilterListVisibility($event)"
    >
      <div
        class="relative pr-0.5 attribute-filters"
        :class="{
          'max-h-72 overflow-x-hidden overflow-y-scroll pb-6': isScrollVisible,
        }"
      >
        <ClientOnly>
          <div
            v-for="(item, index) in valuesToDisplay"
            :key="index"
            class="flex items-center justify-between mb-2 last:mb-0"
            :class="{ 'pr-5': isScrollVisible }"
          >
            <CCheckbox
              class="w-full min-w-0 pr-2 text-xs"
              :checked="item.selected"
              data-test="filter-checkbox"
              @update:checked="toggleFilter(item.item)"
              @keyup.enter="toggleFilter(item.item)"
            >
              <span class="truncate" :title="item.displayValue || item.item">{{
                item.displayValue || item.item
              }}</span></CCheckbox
            >
            <span class="text-xs text-gray-800">{{ item.count }}</span>
          </div>
        </ClientOnly>
        <span
          v-if="isShowMoreVisible"
          class="flex text-xs font-bold text-gray-800 cursor-pointer"
          @click="toggleAllValues()"
        >
          {{ t('view_more') }}
          <CIcon class="pl-2 text-gray-800" name="expand_more" />
        </span>
      </div>
    </CollapsableSection>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { Facet } from '@/types/products';

const VALUES_DISPLAY_LIMIT = 6;
const SHOW_MORE_BUTTON_MIN_ITEMS = 6;
const SHOW_SCROLLBAR_MIN_ITEMS = 12;

type Props = {
  facet?: Facet;
  displayName?: string;
};

const props = withDefaults(defineProps<Props>(), {
  facet: undefined,
  displayName: '',
});

const { mq } = useMediaQueries();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const showAllValues = ref(false);
const isFilterListExpanded = ref(false);

const isShowMoreVisible = computed(() => {
  return props.facet?.items.length > SHOW_MORE_BUTTON_MIN_ITEMS && !showAllValues.value;
});

const isScrollVisible = computed(() => {
  return props.facet?.items.length > SHOW_SCROLLBAR_MIN_ITEMS && showAllValues.value && !mq.value.xsmall;
});

const valuesToDisplay = computed(() => {
  const valuesWithProductCount = props.facet?.items.filter((item) => item.count !== 0);
  valuesWithProductCount.sort((itemA, itemB) => sortAttributes('' + itemA.item, '' + itemB.item));

  if (showAllValues.value) {
    return valuesWithProductCount;
  }
  return valuesWithProductCount.slice(0, VALUES_DISPLAY_LIMIT);
});

const hasSelectedValues = computed(() => {
  return props.facet?.items.some((item) => item.selected === true);
});

const toggleFilter = (filterValue) => {
  const query = { ...route.query };
  const { name } = props.facet as Facet;

  if (query[name]) {
    const filterValues = Array.isArray(query[name]) ? [...query[name]] : [query[name]];
    const valueIndex = filterValues.findIndex((existingValue) => existingValue === filterValue.toString());

    if (valueIndex === -1) {
      filterValues.push(filterValue);
    } else {
      filterValues.splice(valueIndex, 1);
    }

    if (!filterValues.length) {
      delete query[name];
    } else {
      Object.assign(query, { [name]: filterValues });
    }
  } else {
    Object.assign(query, { [name]: [filterValue] });
  }

  const newRoute = {
    path: route.path,
    query,
  };

  router.push(newRoute);
};

const setFilterListVisibility = (val: boolean) => {
  isFilterListExpanded.value = val;
};

const toggleAllValues = () => {
  showAllValues.value = true;
};

watch(
  () => props.facet,
  () => {
    isFilterListExpanded.value = hasSelectedValues.value;
  },
);

onMounted(() => {
  isFilterListExpanded.value = hasSelectedValues.value;
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'SideNavAttributeFilter',
});
</script>

<style lang="scss" scoped>
.attribute-filters {
  &::-webkit-scrollbar {
    @apply w-1.5 p-1;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-gray-800 rounded;
  }
}
</style>
